import React, { useState, useRef } from 'react';

const SipStepUpCalculatorSection = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(15000);
  const [incrementType, setIncrementType] = useState('percentage'); // 'percentage' or 'amount'
  const [yearlyIncrement, setYearlyIncrement] = useState(10);
  const [investmentPeriod, setInvestmentPeriod] = useState(15);
  const [expectedReturn, setExpectedReturn] = useState(15);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [regularSIPValue, setRegularSIPValue] = useState(null);
  const resultRef = useRef(null);

  const calculateStepUpSIP = () => {
    const monthlyRate = expectedReturn / 100 / 12;
    let stepUpFutureValue = 0;
    let totalInvestment = 0;
    let currentMonthlyInvestment = monthlyInvestment;

    // Calculate regular SIP (without step-up) for comparison
    const regularSIPFutureValue = monthlyInvestment * (((Math.pow(1 + monthlyRate, investmentPeriod * 12) - 1) / monthlyRate) * (1 + monthlyRate));
    const regularSIPTotalInvestment = monthlyInvestment * investmentPeriod * 12;

    // Generate chart data and calculate step-up SIP
    const chartPoints = [];
    let yearlyInvestments = [];
    
    for (let year = 1; year <= investmentPeriod; year++) {
      let yearlyInvestment = 0;
      
      // Calculate for 12 months of this year
      for (let month = 1; month <= 12; month++) {
        const months = (year - 1) * 12 + month;
        const remainingMonths = investmentPeriod * 12 - months + 1;
        
        // Add current month investment and compound it for remaining months
        stepUpFutureValue += currentMonthlyInvestment * Math.pow(1 + monthlyRate, remainingMonths - 1);
        totalInvestment += currentMonthlyInvestment;
        yearlyInvestment += currentMonthlyInvestment;
      }
      
      yearlyInvestments.push(yearlyInvestment);
      
      // Calculate accumulated value at end of each year for chart
      let accumulatedValue = 0;
      let tempMonthlyInvestment = monthlyInvestment;
      
      for (let y = 1; y <= year; y++) {
        for (let m = 1; m <= 12; m++) {
          const monthsFromStart = (y - 1) * 12 + m;
          const remainingMonthsFromYear = year * 12 - monthsFromStart + 1;
          accumulatedValue += tempMonthlyInvestment * Math.pow(1 + monthlyRate, remainingMonthsFromYear - 1);
        }
        
        if (y < year) {
          if (incrementType === 'percentage') {
            tempMonthlyInvestment = tempMonthlyInvestment * (1 + yearlyIncrement / 100);
          } else {
            tempMonthlyInvestment = tempMonthlyInvestment + yearlyIncrement;
          }
        }
      }
      
      // Calculate regular SIP value at this year
      const regularSIPAtYear = monthlyInvestment * (((Math.pow(1 + monthlyRate, year * 12) - 1) / monthlyRate) * (1 + monthlyRate));
      
      chartPoints.push({
        year,
        stepUpAccumulated: accumulatedValue,
        regularSIPAccumulated: regularSIPAtYear,
        totalInvested: yearlyInvestments.slice(0, year).reduce((a, b) => a + b, 0)
      });
      
      // Increase investment for next year (except for the last year)
      if (year < investmentPeriod) {
        if (incrementType === 'percentage') {
          currentMonthlyInvestment = currentMonthlyInvestment * (1 + yearlyIncrement / 100);
        } else {
          currentMonthlyInvestment = currentMonthlyInvestment + yearlyIncrement;
        }
      }
    }

    const wealthGained = stepUpFutureValue - totalInvestment;
    const regularSIPWealthGained = regularSIPFutureValue - regularSIPTotalInvestment;
    const additionalReturn = stepUpFutureValue - regularSIPFutureValue;
    
    setChartData(chartPoints);
    setRegularSIPValue(Math.round(regularSIPFutureValue));
    setResult({
      futureValue: Math.round(stepUpFutureValue),
      totalInvestment: Math.round(totalInvestment),
      wealthGained: Math.round(wealthGained),
      additionalReturn: Math.round(additionalReturn)
    });

    // Scroll to results section
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const styles = {
    sipCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    radioGroup: {
      display: 'flex',
      gap: '2rem',
      margin: '1rem 0',
      alignItems: 'center'
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer'
    },
    radioInput: {
      width: '16px',
      height: '16px',
      cursor: 'pointer'
    },
    radioLabel: {
      fontSize: '0.9rem',
      color: '#374151',
      cursor: 'pointer'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    resultSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '15px',
      border: '1px solid #e5e7eb'
    },
    chartSection: {
      marginBottom: '1rem'
    },
    chartTitle: {
      fontSize: '1.1rem',
      fontWeight: '700',
      color: '#1a1a1a',
      marginBottom: '0.5rem',
      textAlign: 'center'
    },
    chartContainer: {
      position: 'relative',
      height: '300px',
      backgroundColor: 'white',
      borderRadius: '10px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '1rem'
    },
    summarySection: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1.5rem',
      marginBottom: '1rem'
    },
    summaryLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    summaryRight: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    summaryItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.5rem 0',
      borderBottom: '1px solid #e5e7eb'
    },
    summaryLabel: {
      fontSize: '0.8rem',
      color: '#6b7280',
      fontWeight: '500'
    },
    summaryValue: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '1rem',
      textAlign: 'center'
    },
    resultItem: {
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    resultLabel: {
      fontSize: '0.9rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: '500'
    },
    resultValue: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#1a1a1a'
    },
    recalculateButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '0.4rem 1.2rem',
      borderRadius: '18px',
      fontSize: '0.8rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'flex-start',
      marginTop: '0.5rem'
    }
  };

  // SVG Growth Chart Component
  const StepUpGrowthChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => d.stepUpAccumulated));
    const chartWidth = 760;
    const chartHeight = 300;
    const padding = 60;
    const plotWidth = chartWidth - padding * 2;
    const plotHeight = chartHeight - padding * 2;

    const xScale = (year) => padding + (year / investmentPeriod) * plotWidth;
    const yScale = (value) => chartHeight - padding - (value / maxValue) * plotHeight;

    // Create path for step-up accumulated amount (upper area)
    const stepUpPath = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.stepUpAccumulated)}`
    ).join(' ') + ` L ${xScale(investmentPeriod)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

    // Create path for regular SIP (lower area)
    const regularSIPPath = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.regularSIPAccumulated)}`
    ).join(' ') + ` L ${xScale(investmentPeriod)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

    // Create line for step-up accumulated amount
    const stepUpLine = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.stepUpAccumulated)}`
    ).join(' ');

    // Generate Y-axis labels
    const yLabels = [];
    for (let i = 0; i <= 6; i++) {
      const value = (maxValue / 6) * i;
      yLabels.push({
        value: Math.round(value),
        y: yScale(value)
      });
    }

    return (
      <svg width="100%" height="300" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
        {/* Grid lines */}
        <defs>
          <pattern id="stepup-grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width={chartWidth} height={chartHeight} fill="url(#stepup-grid)" />
        
        {/* Y-axis labels */}
        {yLabels.map((label, i) => (
          <g key={i}>
            <line x1={padding} y1={label.y} x2={chartWidth - padding} y2={label.y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padding - 10} y={label.y + 5} textAnchor="end" fontSize="10" fill="#6b7280">
              {(label.value / 1000000).toFixed(1)}M
            </text>
          </g>
        ))}
        
        {/* X-axis labels */}
        {data.map((d, i) => (
          <g key={i}>
            {i % 3 === 0 && (
              <>
                <line x1={xScale(d.year)} y1={chartHeight - padding} x2={xScale(d.year)} y2={chartHeight - padding + 5} stroke="#6b7280" strokeWidth="1" />
                <text x={xScale(d.year)} y={chartHeight - padding + 15} textAnchor="middle" fontSize="10" fill="#6b7280">
                  {d.year}
                </text>
              </>
            )}
          </g>
        ))}
        
        {/* Regular SIP area (lower) */}
        <path d={regularSIPPath} fill="#3b82f6" opacity="0.6" />
        
        {/* Step-up SIP area (upper) */}
        <path d={stepUpPath} fill="#06b6d4" opacity="0.3" />
        
        {/* Step-up line */}
        <path d={stepUpLine} fill="none" stroke="#06b6d4" strokeWidth="2" />
        
        {/* Axis labels */}
        <text x={chartWidth / 2} y={chartHeight - 5} textAnchor="middle" fontSize="12" fontWeight="600" fill="#374151">
          Period in Years
        </text>
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="12" fontWeight="600" fill="#374151" transform={`rotate(-90, 20, ${chartHeight / 2})`}>
          Amount (Rs.)
        </text>
        
        {/* Legend */}
        <g transform={`translate(${chartWidth - 200}, 20)`}>
          <rect x="0" y="0" width="12" height="12" fill="#06b6d4" />
          <text x="16" y="10" fontSize="10" fill="#374151">Step-up SIP</text>
          <rect x="0" y="18" width="12" height="12" fill="#3b82f6" />
          <text x="16" y="28" fontSize="10" fill="#374151">Regular SIP</text>
        </g>
      </svg>
    );
  };

  return (
    <>
      <style>{`
        .sip-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-calculate-btn:hover {
          background-color: #dc2626 !important;
        }
        
        .recalculate-btn:hover {
          background-color: #4b5563 !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .result-grid {
            grid-template-columns: 1fr !important;
            gap: 0.5rem !important;
          }
          .radio-group {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 1rem !important;
          }
          .summary-section {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
        }
      `}</style>
      
      <div style={styles.sipCalculatorContent}>
        <p style={styles.description}>
          This calculator will help you visualize the amount accumulated with Regular Investment with an Annual Increase of your SIP amount (Step-up SIP)
        </p>
        
        {/* Monthly Investment */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Monthly Investment (Rs.)
            <input
              type="number"
              min="500"
              max="100000"
              step="500"
              value={monthlyInvestment}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setMonthlyInvestment('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setMonthlyInvestment(Math.max(1000, Math.min(100000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setMonthlyInvestment(15000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1000"
              max="100000"
              step="1000"
              value={monthlyInvestment || 15000}
              onChange={(e) => setMonthlyInvestment(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1K</span>
              <span>25K</span>
              <span>50K</span>
              <span>75K</span>
              <span>1L</span>
            </div>
          </div>
        </div>

        {/* Increment Type Radio Buttons */}
        <div style={{...styles.radioGroup}} className="radio-group">
          <div style={styles.radioOption}>
            <input
              type="radio"
              id="percentage"
              name="incrementType"
              value="percentage"
              checked={incrementType === 'percentage'}
              onChange={(e) => setIncrementType(e.target.value)}
              style={styles.radioInput}
            />
            <label htmlFor="percentage" style={styles.radioLabel}>
              Increment by (%)
            </label>
          </div>
          <div style={styles.radioOption}>
            <input
              type="radio"
              id="amount"
              name="incrementType"
              value="amount"
              checked={incrementType === 'amount'}
              onChange={(e) => setIncrementType(e.target.value)}
              style={styles.radioInput}
            />
            <label htmlFor="amount" style={styles.radioLabel}>
              Increment by (Rs)
            </label>
          </div>
        </div>

        {/* Yearly Increment */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Yearly Increment ({incrementType === 'percentage' ? '%' : 'Rs.'})
            <input
              type="number"
              min={incrementType === 'percentage' ? 1 : 1000}
              max={incrementType === 'percentage' ? 25 : 50000}
              step={incrementType === 'percentage' ? 1 : 1000}
              value={yearlyIncrement}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setYearlyIncrement('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    const minVal = incrementType === 'percentage' ? 1 : 1000;
                    const maxVal = incrementType === 'percentage' ? 25 : 50000;
                    setYearlyIncrement(Math.max(minVal, Math.min(maxVal, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setYearlyIncrement(10);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder={incrementType === 'percentage' ? 'Enter %' : 'Enter amount'}
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min={incrementType === 'percentage' ? 1 : 1000}
              max={incrementType === 'percentage' ? 25 : 50000}
              step={incrementType === 'percentage' ? 1 : 1000}
              value={yearlyIncrement}
              onChange={(e) => setYearlyIncrement(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              {incrementType === 'percentage' ? (
                <>
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                  <span>15</span>
                  <span>20</span>
                  <span>25</span>
                </>
              ) : (
                <>
                  <span>1k</span>
                  <span>12.5k</span>
                  <span>25k</span>
                  <span>37.5k</span>
                  <span>50k</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Investment Period */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Investment period (in years)
            <input
              type="number"
              min="1"
              max="30"
              step="1"
              value={investmentPeriod}
              onChange={(e) => {
                const value = Math.max(1, Math.min(30, parseInt(e.target.value) || 1));
                setInvestmentPeriod(value);
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter years"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={investmentPeriod}
              onChange={(e) => setInvestmentPeriod(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
              <span>25</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (% p.a)
            <input
              type="number"
              min="1"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => {
                const value = Math.max(1, Math.min(30, parseFloat(e.target.value) || 1));
                setExpectedReturn(value);
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="sip-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>6</span>
              <span>12</span>
              <span>18</span>
              <span>24</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="sip-calculate-btn"
          onClick={calculateStepUpSIP}
        >
          Calculate
        </button>

        {/* Results */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            {/* Growth Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.chartTitle}>Growth Chart</h3>
              <StepUpGrowthChart data={chartData} />
            </div>

            {/* Summary Section */}
            <div style={styles.summarySection} className="summary-section">
              <div style={styles.summaryLeft}>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Monthly Investment (Rs.)</span>
                  <span style={styles.summaryValue}>{monthlyInvestment.toLocaleString()}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Investment Period (Years)</span>
                  <span style={styles.summaryValue}>{investmentPeriod}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Step-Up Investment(%)</span>
                  <span style={styles.summaryValue}>
                    {incrementType === 'percentage' ? `${yearlyIncrement}%` : `₹${yearlyIncrement.toLocaleString()}`}
                  </span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Rate of Return (%)</span>
                  <span style={styles.summaryValue}>{expectedReturn}</span>
                </div>
              </div>
              <div style={styles.summaryRight}>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Total Invested Amount (Rs)</span>
                  <span style={styles.summaryValue}>{result.totalInvestment.toLocaleString()}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>SIP Value without Top-Up (Rs)</span>
                  <span style={styles.summaryValue}>{regularSIPValue.toLocaleString()}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>SIP Value with Top-Up</span>
                  <span style={styles.summaryValue}>{result.futureValue.toLocaleString()}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Estimated Return</span>
                  <span style={styles.summaryValue}>{result.wealthGained.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Re-calculate Button */}
            <button
              style={styles.recalculateButton}
              className="recalculate-btn"
              onClick={calculateStepUpSIP}
            >
              Re-Calculate
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default SipStepUpCalculatorSection;